import React from 'react';
import { useTranslation } from 'react-i18next';
import {IconTemplate, IconTrash} from '@tabler/icons-react';
import styles from './ProjectList.module.scss';
import type {ProjectListProject} from './ProjectList';

interface ProjectCardViewProps {
    project: ProjectListProject;
  onDelete?: (projectId: string, event: React.MouseEvent) => void;
    onTemplateCreate?: (project: ProjectListProject, event: React.MouseEvent) => void;
  onClick?: () => void;
}

const ProjectCardView: React.FC<ProjectCardViewProps> = ({project, onDelete, onTemplateCreate, onClick}) => {
  const { t } = useTranslation('project');
  const getStatusBadgeClass = (progress: number) => {
    if (progress === 0) {
      return styles.statusPlaceholder;
    } else if (progress === 100) {
      return styles.statusCompleted;
    } else {
      return styles.statusInProgress;
    }
  };

  const getStatusText = (progress: number): string => {
    if (progress === 0) {
      return 'Planned';
    } else if (progress === 100) {
      return 'Done';
    } else {
      return 'In Progress';
    }
  };

  const getMethodBadgeClass = (method?: string) => {
    switch (method) {
      case 'AGILE':
        return styles.agileMethodBadge;
      case 'CLASSIC':
        return styles.classicMethodBadge;
      case 'HYBRID':
        return styles.hybridMethodBadge;
      default:
        return styles.agileMethodBadge;
    }
  };

  const formatMethod = (method?: string): string => {
    switch (method) {
      case 'AGILE':
        return 'Agile';
      case 'CLASSIC':
        return 'Classic';
      case 'HYBRID':
        return 'Hybrid';
      default:
        return 'N/A';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).replace(',', ' |');
  };

  return (
    <div className={styles.projectCardView} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className={styles.cardHeader}>
        <img src={project.logoUrl} alt={project.title} className={styles.cardLogo} />
        <div className={styles.cardInfo}>
          <h3 className={styles.cardTitle}>{project.title}</h3>
          <p className={styles.cardDescription}>{project.description}</p>
        </div>
          <div className={styles.cardActionGroup}>
              {onTemplateCreate && (
                  <button
                      className={styles.cardTemplateButton}
                      onClick={(e) => onTemplateCreate(project, e)}
                      title={t('list.actions.createTemplate')}
                  >
                      <IconTemplate size={16}/>
                  </button>
              )}
              {onDelete && (
                  <button
                      className={styles.cardDeleteButton}
                      onClick={(e) => onDelete(project.id, e)}
                      title={t('list.card.deleteTitle')}
                  >
                      <IconTrash size={16}/>
                  </button>
              )}
          </div>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.cardRow}>
          <div className={styles.cardField}>
            <span className={styles.cardLabel}>{t('list.card.method')}</span>
            <div className={`${styles.badge} ${getMethodBadgeClass(project.method)}`}>
              {formatMethod(project.method)}
            </div>
          </div>
          <div className={styles.cardField}>
            <span className={styles.cardLabel}>{t('list.card.status')}</span>
            <div className={`${styles.badge} ${getStatusBadgeClass(project.progress)}`}>
              {getStatusText(project.progress)}
            </div>
          </div>
        </div>

        <div className={styles.cardRow}>
          <div className={styles.cardField}>
            <span className={styles.cardLabel}>{t('list.card.characters')}</span>
            <div className={styles.cardCharacterGroup}>
              {project.teamMembers.length > 0 ? (
                <>
                  {project.teamMembers.slice(0, 3).map((member) => (
                    <span
                      key={member.id}
                      className={styles.cardCharacterName}
                      title={member.name}
                    >
                      {member.name}
                    </span>
                  ))}
                  {project.teamMembers.length > 3 && (
                    <span className={styles.cardCharacterMore}>
                      {t('list.moreCharacters', { count: project.teamMembers.length - 3 })}
                    </span>
                  )}
                </>
              ) : (
                <span className={styles.cardNoCharacters}>{t('list.noCharacters')}</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.cardRow}>
          <div className={styles.cardField}>
            <span className={styles.cardLabel}>{t('list.card.lastEdit')}</span>
            <div className={styles.cardLastEdit}>
              <span className={styles.cardDate}>{formatDate(project.lastEdit.date)}</span>
              <span className={styles.cardEditor}>{project.lastEdit.editor}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.cardFooter}>
        <div className={styles.cardProgress}>
          <div className={styles.cardProgressTrack}>
            <div 
              className={`${styles.cardProgressFill} ${
                project.progress === 100 ? styles.progressFull :
                project.progress >= 50 ? styles.progressMedium :
                styles.progressLow
              }`}
              style={{ width: `${project.progress}%` }}
            />
          </div>
          <span className={styles.cardProgressText}>{project.progress}%</span>
        </div>
      </div>
    </div>
  );
};

export default ProjectCardView; 
